import { createAppKit } from '@reown/appkit/react';
import { QueryClient } from '@tanstack/react-query';
import { PersistQueryClientProvider } from '@tanstack/react-query-persist-client';
import { createSyncStoragePersister } from '@tanstack/query-sync-storage-persister';
import { WagmiProvider, type State, useAccount, useChainId } from 'wagmi';
import { wagmiAdapter, metadata, projectId, networks } from '../config/wagmi';
import { ReactNode, useEffect, useRef } from 'react';
import type { AppKitNetwork } from '@reown/appkit/networks';

// Try importing useAppKit first (v1.8.14 may have renamed the hook)
// If this fails during build, we'll fallback to useWeb3Modal
import { useAppKit } from '@reown/appkit/react';


// Optimized QueryClient singleton with proper configuration
let queryClient: QueryClient;

export function getQueryClient(): QueryClient {
  if (!queryClient) {
    queryClient = new QueryClient({
      defaultOptions: {
        queries: {
          staleTime: 60 * 1000, // 1 minute
          gcTime: 10 * 60 * 1000, // 10 minutes (renamed from cacheTime in newer versions)
          retry: 3,
          retryDelay: attemptIndex => Math.min(1000 * 2 ** attemptIndex, 30000),
        },
        mutations: {
          retry: 1,
        },
      },
    });
  }
  return queryClient;
}

// Create localStorage persister for crypto icons
// Icons are immutable and cached indefinitely, so we persist them
const localStoragePersister = createSyncStoragePersister({
  storage: typeof window !== 'undefined' ? window.localStorage : undefined,
  key: 'QINV_QUERY_CACHE', // Prefix to avoid conflicts with other apps
});

// Create AppKit with Reown v1.8.14 API
// Migration from Web3Modal to Reown AppKit (Phase 3)
// ⚠️ CORRECTION: v1.8.14 actually REQUIRES projectId and networks explicitly

// DEBUG: Log AppKit configuration before creation
createAppKit({
  // Array of adapters - WagmiAdapter configuration
  adapters: [wagmiAdapter],

  // ✅ REQUIRED: projectId must be passed explicitly (contrary to migration guide assumption)
  projectId,

  // ✅ REQUIRED: networks must be passed explicitly
  // Import networks directly from wagmi.ts (exported AppKitNetwork[])
  // Type assertion to satisfy non-empty tuple requirement
  networks: networks as [AppKitNetwork, ...AppKitNetwork[]],

  // Metadata moved from wagmi.ts (v1.8.14 requires it here, not in adapter)
  metadata,

  // Coinbase Wallet configuration
  // ENABLED: Coinbase Smart Wallet now works via EIP-5792 batched transactions
  //
  // Previous issue (resolved):
  // - 2-step flow (approve + mint) caused callback loss on second popup
  // - With EIP-5792 batching: approve+mint = 1 atomic transaction = no second popup
  //
  // With enableCoinbase: true:
  // - Coinbase Smart Wallet connects natively (no QR code needed)
  // - EIP-5792 wallet_sendCalls batches approve+mint into 1 popup
  // - Wallets without batching support fall back to 2-step flow automatically
  enableCoinbase: true,

  // Theme configuration (same as Web3Modal v5)
  // Note: Reown AppKit requires hex color values, CSS variables not supported
  // Color matches --primary CSS variable: hsl(259 82% 57%)
  themeMode: 'dark',
  themeVariables: {
    '--w3m-accent': '#7D39EB', // Design system primary color
    '--w3m-font-family': 'Satoshi, system-ui, sans-serif',
    '--w3m-border-radius-master': '3px'
  },

  // Curated wallet list - Coinbase Smart Wallet enabled natively via EIP-5792
  // See: https://docs.reown.com/appkit/react/core/options
  includeWalletIds: [
    'fd20dc426fb37566d803205b19bbc1d4096b248ac04548e3cfb6b3a38bd033aa', // Coinbase Wallet (native SDK)
    'c57ca95b47569778a828d19178114f4db188b89b763c899ba0be274e97267d96', // MetaMask
    '4622a2b2d6af1c9844944291e5e7351a6aa24cd7b23099efac1b2fd875da31a0', // Trust Wallet
    '971e689d0a5be527bac79629b4ee9b925e82208e5168b733496a09c0faed0709', // OKX Wallet (on-chain)
    'a797aa35c0fadbfc1a53e7f675162ed5226968b44a19ee3d24385c64d1d3c393', // Phantom
  ],
  featuredWalletIds: [
    'fd20dc426fb37566d803205b19bbc1d4096b248ac04548e3cfb6b3a38bd033aa', // Coinbase Wallet (featured first - BASE official)
    'c57ca95b47569778a828d19178114f4db188b89b763c899ba0be274e97267d96', // MetaMask
    '4622a2b2d6af1c9844944291e5e7351a6aa24cd7b23099efac1b2fd875da31a0', // Trust Wallet
    '971e689d0a5be527bac79629b4ee9b925e82208e5168b733496a09c0faed0709', // OKX Wallet (on-chain)
    'a797aa35c0fadbfc1a53e7f675162ed5226968b44a19ee3d24385c64d1d3c393', // Phantom
  ],

  // Hide "All Wallets" - our curated list is complete
  allWallets: 'HIDE',

  // Features configuration - Social login disabled
  features: {
    email: false,                        // Disable email authentication
    socials: [],                         // Social logins disabled (Google, X, Apple removed)
    analytics: true,                     // DEBUG: Temporarily enabled to investigate errors
    onramp: false,                       // Disable on-ramp (also blocks Coinbase tracking)
  },

  // Mobile-specific configurations to improve wallet reconnection
  // These settings help with mobile wallet deep linking and session persistence
  enableWalletConnect: true,             // Ensure WalletConnect is enabled
  enableInjected: true,                  // Enable injected wallet detection
  enableEIP6963: true,                   // Enable EIP-6963 wallet discovery (multi-wallet standard)

  // Network switching is exposed via a dedicated <w3m-network-button> next to the
  // wallet button in the header, so hide the duplicate switcher inside the account modal.
  enableNetworkSwitch: false,
});

interface Web3ModalProviderProps {
  children: ReactNode;
  initialState?: State;
}

/**
 * ConnectionListener - Internal component to monitor wallet connection changes
 *
 * This component runs inside WagmiProvider context and listens for:
 * - Account connection/disconnection
 * - Network/chain changes
 *
 * When a change is detected:
 * 1. Closes the Reown modal automatically
 * 2. Invalidates React Query cache to trigger fresh balance queries
 * 3. Forces UI updates without requiring manual refresh (F5)
 */
function ConnectionListener({ children }: { children: ReactNode }) {
  const { address, isConnected } = useAccount();
  const chainId = useChainId();
  // v1.8.14: useWeb3Modal renamed to useAppKit
  const { close } = useAppKit();

  // Track if we've already processed this connection to avoid duplicate actions
  const processedConnection = useRef<string | null>(null);

  useEffect(() => {
    if (isConnected && address) {
      // Check if we've already processed this specific connection
      const connectionKey = `${address}-${chainId}`;
      if (processedConnection.current === connectionKey) {
        return; // Already processed, skip
      }

      // Mark as processed
      processedConnection.current = connectionKey;

      // IMPORTANT: Close the modal first to improve UX
      // Use setTimeout to defer closing until after current render cycle completes
      // This prevents Lit warning about updates scheduled during updates
      setTimeout(() => {
        close();
      }, 0);

      // Invalidate cache to fetch fresh balances
      const client = getQueryClient();

      // Invalidate balance-related queries to force refetch from fork RPC
      client.invalidateQueries({ queryKey: ['token-balance'] });
      client.invalidateQueries({ queryKey: ['wallet-balances'] });
      client.invalidateQueries({ queryKey: ['balance'] }); // wagmi useBalance query key

      // Earnings prefetch was previously keyed on `VITE_VAULT_ADDRESS` (a single
      // env-backed vault). With multi-chain per ADR-0007 there is no longer a
      // canonical "default vault" at connect time — the right vault is known
      // only after the user selects a portfolio. The dashboard fetches earnings
      // lazily when the user navigates there; the marginal latency is not worth
      // the complexity of guessing which portfolio to prefetch for.
    } else if (!isConnected) {
      // Reset when disconnected
      processedConnection.current = null;
    }
  }, [isConnected, address, chainId, close]);

  return <>{children}</>;
}


/**
 * WalletConnectErrorListener - Captures WalletConnect connection errors
 * This helps debug issues with Coinbase Wallet redirects on Safari iOS
 */
function WalletConnectErrorListener({ children }: { children: ReactNode }) {
  const { connector, isConnected } = useAccount();

  useEffect(() => {
    if (!connector) return;

    // Log connector state for debugging
    // Listen for connector errors (WalletConnect timeout, redirect failures, etc.)
    const handleError = (_error: Error) => {
    };

    // @ts-ignore - connector.on might not have proper types
    connector.on?.('error', handleError);

    return () => {
      // @ts-ignore
      connector.off?.('error', handleError);
    };
  }, [connector, isConnected]);

  return <>{children}</>;
}

export default function Web3ModalProvider({
  children,
  initialState
}: Web3ModalProviderProps) {
  return (
    <WagmiProvider config={wagmiAdapter.wagmiConfig} initialState={initialState}>
      <PersistQueryClientProvider
        client={getQueryClient()}
        persistOptions={{
          persister: localStoragePersister,
          maxAge: 24 * 60 * 60 * 1000, // 24 hours - crypto icons don't change
          dehydrateOptions: {
            // Only persist crypto icon queries (immutable data)
            shouldDehydrateQuery: (query) => {
              return query.queryKey[0] === 'crypto-icon';
            },
          },
        }}
      >
        <WalletConnectErrorListener>
          <ConnectionListener>
            {children}
          </ConnectionListener>
        </WalletConnectErrorListener>
      </PersistQueryClientProvider>
    </WagmiProvider>
  );
}